import React, { useEffect, useState } from 'react';
import PurchaseCta from '../purchase-cta';
import Payment from '../payment-element/payment';
import EmailField from './emailField';
import SavedCardsContainer from '../saved-cards-container';
import CustomContainerA from './customContainerA';
import CustomContainerB from './customContainerB';
import { saveDefaultPaymentMethod, SetupIntent, createParentAndAddChild } from '../../../services/parent-api';
import DeviceLayoutManager from './deviceLayoutManager';
import SavedPaymentMethodsContainer from '../saved-payment-methods-container';
import {deleteItemFromLocalStorage, REGEX_COLLECTION } from '../../../utils/web-helper';
import getPaymentElementConfigurations from './getPaymentElementConfigurations';
import refreshProductPurchaseContext from './refreshProductPurchaseContext';

const ConfigurePayment = ({
  product,
  paymentHandler,
  settings,
  stripePaymentElement,
  refreshPurchaseContext,
}) => {
  const [clientSecret, setClientSecret] = useState('');
  const [postRefreshError, setPostRefreshError] = useState(stripePaymentElement?.error);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const paymentElementConfig = getPaymentElementConfigurations();

  useEffect(async () => {
    const response = await SetupIntent({
      product_id: product.id,
      source: 'product_purchase',
    }, true);
    setClientSecret(response.client_secret);
  }, []);

  useEffect (() => {
    const isStripeCardsPresent = settings.stripeCards && settings.stripeCards.length > 0;
    const isPaymentMethodsPresent = settings.stripePaymentMethods && settings.stripePaymentMethods.length > 0

    if (clientSecret && (isStripeCardsPresent || isPaymentMethodsPresent)) {
      scrollToTop(1000)
    }
  },[clientSecret, settings.stripeCards, settings.stripePaymentMethods]);

  //server side params - linked_parent_id details will simply login to check if parent exists
  const isEmailFieldRequired = (settings.linked_parent_id == null);
 
  const defaultCardId = () => {
    let cards = settings.stripeCards;
    if (!cards || cards.length == 0) {
      return null;
    } else {
      var defaultCard = cards.filter(function (card) {
        return card.is_default;
      })[0];
      if (!defaultCard) {
        defaultCard = cards[0];
      }
      return defaultCard.stripe_id;
    }
  };

  const defaultPaymentMethodId = () => {
    let paymentMethods = settings.stripePaymentMethods;
    if (!paymentMethods || paymentMethods.length == 0) {
      return null
    }
    let defaultPaymentMethod = paymentMethods.filter((paymentMethod) => {
      return paymentMethod.is_default;
    })
    defaultPaymentMethod = defaultPaymentMethod && defaultPaymentMethod[0];
    if (!defaultPaymentMethod) {
      defaultPaymentMethod = paymentMethods[0];
    }
    return defaultPaymentMethod.stripe_id
  };

  const [selectedCardId, setSelectedCardId] = useState(() => defaultCardId());
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(() => defaultPaymentMethodId());
  const stripeCardsCount = settings?.stripeCards?.length || 0;
  const stripePaymentMethodsCount = settings?.stripePaymentMethods?.length || 0;
  const showSavedCardsContainer = stripeCardsCount > 0 && stripePaymentMethodsCount === 0;
  const showPaymentMethodsContainer = stripePaymentMethodsCount > 0;
  let showPaymentElement =
    (stripeCardsCount === 0 && stripePaymentMethodsCount === 0) ||
    (showSavedCardsContainer && !selectedCardId) ||
    (showPaymentMethodsContainer && !selectedPaymentMethodId);

 

  const purchaseFormSubmitErrorEvent = (properties = {}) => {
    const commonProperties = {
      "Stripe Payment Element Rendered": "New Payment Element"
    }
    trackAnalyticsEvent("Payment Form Error", {
      ...commonProperties,
      ...properties,
    })
  }

  const savedCardsHandler = (data) => {
    if (!data) {
      setPostRefreshError(null)
      trackAnalyticsEvent("Interacted Plan Purchase Combined Page", {
        "Clicked On": "Add Another Payment Method"
      })
    } else {
      trackAnalyticsEvent("Interacted Plan Purchase Combined Page", {
        "Clicked On": "Saved Payment Method"
      })
    }
    setSelectedCardId(data);
  };

  const selectPaymentMethod = (data) => {
    if (!data) {
      setPostRefreshError(null)
      trackAnalyticsEvent("Interacted Plan Purchase Combined Page", {
        "Clicked On": "Add Another Payment Method"
      })
    } else {
      trackAnalyticsEvent("Interacted Plan Purchase Combined Page", {
        "Clicked On": "Saved Payment Method"
      })
    }
    setSelectedPaymentMethodId(data)
  }

  const savedCardsPaymentHandler = (setError) => {
    if (!showSavedCardsContainer) {
      return;
    }
    if (!selectedCardId) {
      return;
    }
    let paymentElementProperties = {
      'Stripe Payment Methods Info': 'cards',
      'Wallet': undefined
    }
    try {
      paymentHandler(
        { stripe_card_id: selectedCardId },
        {
          setButtonDisabled,
          setError,
          paymentElementProperties,
          purchaseFormSubmitErrorEvent,
          scrollToTop
        }
      );
    } catch(e){
      const userError = "Something went wrong. Please try again"
      console.error(e)
      setError(userError);
      setButtonDisabled(false);
      purchaseFormSubmitErrorEvent({
        "Error Message" : userError,
        "Error Step" : "subscribe_product_failed",
        "Error Message From Api" : `api failed with exception, ${JSON.stringify(e)}`,
      })
    }
  };

  const parentCreation = async (email) => {
    let options = {
      user: {
        email: email,
        type: 'parent',
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
    };

    const data = await createParentAndAddChild(options);
    deleteItemFromLocalStorage('parentDobYear');
    if (data.success) {
      return {
        via_remote_login: true,
        parent_id: data.parent_id,
      };
    } else {
      setButtonDisabled(false)
      const userError = data.message || 'Something went wrong, please try again'
      purchaseFormSubmitErrorEvent({
        "Error Message" : userError,
        "Error Step" : "parent_creation_failed",
        "Error Message From Api" : userError,
      })
      refreshPurchaseContext({
        error: userError
      })
      return false;
    }
  };

  const savedPaymentMethodsHandler = async (setError) => {
    if (!showPaymentMethodsContainer) {
      return;
    }
    if (!selectedPaymentMethodId) {
      return;
    }
    
    let paymentElementProperties = {}
    let StripePaymentMethodInfoMap = {
      'card': 'cards',
      'google_pay': 'digital wallets',
      'apple_pay': 'digital wallets'
    }
    let walletMap = {
      'card': null,
      'google_pay': 'googlePay',
      'apple_pay': 'applePay'
    }

    const currentPaymentMethod = settings.stripePaymentMethods.find(
      (pm) => pm.stripe_id === selectedPaymentMethodId
    ) || null

    const type = currentPaymentMethod?.type;
    const cardWalletType = currentPaymentMethod?.card_wallet_type
    
    if (type && !cardWalletType) {
      paymentElementProperties["Stripe Payment Method Info"] = StripePaymentMethodInfoMap[type];
      paymentElementProperties["Wallet"] = undefined
    } else if (type == 'card' && cardWalletType ) {
      paymentElementProperties["Stripe Payment Method Info"] = StripePaymentMethodInfoMap[cardWalletType];
      paymentElementProperties["Wallet"] = walletMap[cardWalletType];
    }

    if (selectedPaymentMethodId !== defaultPaymentMethodId()) {
      try {
        await saveDefaultPaymentMethod({payment_method_id: selectedPaymentMethodId})
      } catch(e) {
        console.error(e)
        const userError = "Something went wrong. Please try again"
        setError(userError)
        setButtonDisabled(false)
        purchaseFormSubmitErrorEvent({
          "Error Message" : userError,
          "Error Step" : "set_default_payment_method_failed",
          "Error Message From Api" : `api failed with exception, ${JSON.stringify(e)}`,
        })
      }
    }

    try {
      paymentHandler(
        { charge_default_payment_method: true },
        {
          setButtonDisabled,
          setError,
          paymentElementProperties,
          purchaseFormSubmitErrorEvent,
          scrollToTop
        }
      );

    } catch (e) {
      console.error(e)
      const userError = "Something went wrong. Please try again"
      setError(userError)
      setButtonDisabled(false)
      purchaseFormSubmitErrorEvent({
        "Error Message" : userError,
        "Error Step" : "subscribe_product_failed",
        "Error Message From Api" : `api failed with exception, ${JSON.stringify(e)}`,
      })
    }

  }

  const emailValidationCallback = (email,  setEmailErrorMessage) => {
    if (!email || !REGEX_COLLECTION.EMAIL_REGEX.test(email)) {
      setEmailErrorMessage('Please enter a valid email address');
      setButtonDisabled(false)
      return false;
    }
    return true;
  }

  const scrollToTop = (timeoutInMs) => {
    setTimeout(() =>{
      const parentElement = document.getElementById("scroll-elt-parent")
      const element = document.getElementById("scroll-elt");
      if (element){
        element.scrollTop = element.scrollHeight;
        setTimeout(() => {
          if (element.scrollTop > 0 && parentElement) {
            parentElement.scrollIntoView({ block: 'end',  behavior: 'smooth' });
          }
        }, 100)
      }
      // make sure overflow property of element is set to scroll before setting element.scrollTop
    } ,timeoutInMs)
  }

  const paymentElementOnReadyCallback = (e) => {
    scrollToTop(500)
  }

  const paymentElementOnChangeCallback = (options = {})  => {
    const lastEvent = options.lastEvent
    const currentEvent = options.currentEvent
    const paymentElementReady = options.paymentElementReady
    const paymentElementFocussedRef = options.paymentElementFocussedRef

    if(!paymentElementReady) return
    if(!paymentElementFocussedRef.current) return
    if (!lastEvent) return;
    
    let typeMap = {
      'card': 'cards',
      'google_pay': 'googlePay',
      'apple_pay': 'applePay'
    }

    // if type is different of prev and curr event then user has changed payment method
    const lastEventType = lastEvent?.value?.type
    const currentEventType = currentEvent?.value?.type

    if (lastEventType && currentEventType && lastEventType !== currentEventType) {
      trackAnalyticsEvent("Interacted Plan Purchase Combined Page", {
        "Clicked On": "Payment Method Tab",
        "Payment Method Clicked": typeMap[currentEventType]
      })
    } else if (lastEventType && currentEventType && lastEventType === currentEventType && currentEventType === "card") {
      if (
        !currentEvent.collapsed &&
        !lastEvent.collapsed &&
        (currentEvent.empty !== lastEvent.empty ||
          currentEvent.complete !== lastEvent.complete)
      ) {
        trackAnalyticsEvent('Interacted Plan Purchase Combined Page', {
          'Interaction Details': 'Started Typing Credit Card Details',
        });
      }
    }

    if (currentEventType !== lastEventType) {
      options?.setErrorMessage(null)
      options?.setEmailErrorMessage(null)
      scrollToTop(500)
    }
  }

  const onEmailError = () => {
    scrollToTop(500)
  }

  const stripeSuccessCallback = async (setupIntent, setErrorMessage, email,paymentElementMetaData) => {
      let params = {};
      
      let paymentElementProperties = {}
      let StripePaymentMethodInfoMap = {
        'card': 'cards',
        'google_pay': 'digital wallets',
        'apple_pay': 'digital wallets'
      }
      let walletMap = {
        'card': null,
        'google_pay': 'googlePay',
        'apple_pay': 'applePay'
      }
      const paymentMethodType = paymentElementMetaData.lastOnChangeEvent?.value?.type
      paymentElementProperties["Stripe Payment Method Info"] = StripePaymentMethodInfoMap[paymentMethodType] || undefined;
      paymentElementProperties["Wallet"] = walletMap[paymentMethodType] || undefined;
  
      if(settings.linked_parent_id == null){
        try {
          const response = await parentCreation(email);
          if(!response){
            return
          }
          response.email_id = email;
          params = { ...params, ...response };
        } catch (e) {
          console.error(e)
          const userError = "Something went wrong. Please try again"
          purchaseFormSubmitErrorEvent({
            "Error Message" : userError,
            "Error Step" : "parent_creation_failed",
            "Error Message From Api" : `api failed with exception, ${JSON.stringify(e)}`,
          })
          setButtonDisabled(false)
          refreshPurchaseContext({
            error: userError
          })
          return
        }
      }else{
        if(settings.is_student_login){
          const loginContextParams = {
            via_remote_login: true,
            parent_id: settings.linked_parent_id
          };
          params = {...params, ...loginContextParams};
        }
      }
      
      try {
        await saveDefaultPaymentMethod({...params, payment_method_id: setupIntent.payment_method});
      } catch(e) {
        console.error(e)
        const userError = "Something went wrong. Please try again"
        purchaseFormSubmitErrorEvent({
          "Error Message" : userError,
          "Error Step" : "set_default_payment_method_failed",
          "Error Message From Api" : `api failed with exception, ${JSON.stringify(e)}`,
        })
        setButtonDisabled(false)
        refreshPurchaseContext({
          error: userError
        })
        return
      }

      try {
        paymentHandler({...params, 'charge_default_payment_method': true}, { paymentElementProperties, refreshPurchaseContext,purchaseFormSubmitErrorEvent });
      } catch (e) {
        console.error(e)
        const userError = "Something went wrong. Please try again"
        purchaseFormSubmitErrorEvent({
          "Error Message" : userError,
          "Error Step" : "subscribe_product_failed",
          "Error Message From Api" : `api failed with exception, ${JSON.stringify(e)}`,
        })
        setButtonDisabled(false)
        refreshPurchaseContext({
          error: userError
        })
        return
      } 
  }

  const stripeErrorCallback = (error, setupIntent,setErrorMessage) => {
    console.error("Stripe Form Error", error);
    setButtonDisabled(false)
    if (error.type === "validation_error") {
      purchaseFormSubmitErrorEvent({
        "Error Message" : error.message,
        "Error Code": error?.code,
        "Error Step" : "confirm_setup_failed",
        "Error Message From Api" : error.message,
      })
      scrollToTop(500)
      return;
    }
    // refresh context if setup intent status was in canceled state
    if (error?.setup_intent?.status === 'canceled') {
      purchaseFormSubmitErrorEvent({
        "Error Message" : error.message,
        "Error Code": error?.code,
        "Error Step" : "confirm_setup_failed",
        "Error Message From Api" : error.message,
        "Setup Intent Status" : error?.setup_intent?.status,
        "Setup Intent Id" : error?.setup_intent?.id
      })
      refreshPurchaseContext({error: error.message})
      return;
    }

    if (error?.code === 'setup_intent_unexpected_state') {
      const userError = "Something went wrong. Please try again"
      purchaseFormSubmitErrorEvent({
        "Error Message" : userError,
        "Error Code": error?.code,
        "Error Step" : "confirm_setup_failed",
        "Error Message From Api" : error.message,
        "Setup Intent Status" : error?.setup_intent?.status,
        "Setup Intent Id" : error?.setup_intent?.id
      })
      refreshPurchaseContext({error: userError})
      return;
    }

    if (setupIntent) {
      purchaseFormSubmitErrorEvent({
        "Error Message" : error.message,
        "Error Step" : "confirm_setup_failed",
        "Error Message From Api" : `setup intent received a status different than succeeded i.e ${setupIntent?.status}`,
        "Setup Intent Status" : setupIntent?.status,
        "Setup Intent Id" : setupIntent?.id
      })
    } else if (error) {
      purchaseFormSubmitErrorEvent({
        "Error Message" : error.message,
        "Error Code": error?.code,
        "Error Step" : "confirm_setup_failed",
        "Error Message From Api" : error.message,
        "Setup Intent Status" : error?.setup_intent?.status,
        "Setup Intent Id" : error?.setup_intent?.id
      })
    }

    setErrorMessage(error.message);
    scrollToTop(500)
  }

  const purchaseCTA = (props) => {
    const wrappedClickHandler = (e) => {
      let paymentElementProperties = {}
      let StripePaymentMethodInfoMap = {
        'card': 'cards',
        'google_pay': 'digital wallets',
        'apple_pay': 'digital wallets'
      }
      let walletMap = {
        'card': null,
        'google_pay': 'googlePay',
        'apple_pay': 'applePay'
      }

      if (showSavedCardsContainer && selectedCardId) {
        paymentElementProperties = {
          'Stripe Payment Methods Info': 'cards',
          'Wallet': undefined
        }
      } else if (showPaymentMethodsContainer && selectedPaymentMethodId) {
        const currentPaymentMethod = settings.stripePaymentMethods.find(
          (pm) => pm.stripe_id === selectedPaymentMethodId
        ) || null
    
        const type = currentPaymentMethod?.type;
        const cardWalletType = currentPaymentMethod?.card_wallet_type
        
        if (type && !cardWalletType) {
          paymentElementProperties["Stripe Payment Method Info"] = StripePaymentMethodInfoMap[type];
          paymentElementProperties["Wallet"] = undefined
        } else if (type == 'card' && cardWalletType ) {
          paymentElementProperties["Stripe Payment Method Info"] = StripePaymentMethodInfoMap[cardWalletType];
          paymentElementProperties["Wallet"] = walletMap[cardWalletType];
        }
      } else {
        const paymentMethodType = props.paymentElementProps.lastOnChangeEvent?.value?.type
        paymentElementProperties["Stripe Payment Method Info"] = StripePaymentMethodInfoMap[paymentMethodType] || undefined;
        paymentElementProperties["Wallet"] = walletMap[paymentMethodType] || undefined;
      }
      
      setButtonDisabled(true);
      trackAnalyticsEvent("Interacted Plan Purchase Combined Page",{
        "Clicked On": "Start Trial",
        ...paymentElementProperties
      })
      trackAnalyticsEvent("Credit Card Submitted", {...paymentElementProperties});
      props.clickHandler && props.clickHandler(e)
    }
    const finalProps = {
      ...props,
      clickHandler: wrappedClickHandler,
      disableBtn: props.disableBtn || buttonDisabled 
    }

    return <PurchaseCta {...finalProps} fromCreditForm={true} settings={settings} />
  }
  


  let elementOptions = {
    appearance: paymentElementConfig.appearance,
    fonts: paymentElementConfig.fonts
  };

  return (
    clientSecret && (
      <Payment
        stripeKey={settings.stripeKey}
        clientSecret={clientSecret}
        elementOptions={elementOptions}
        paymentElementOptions={paymentElementConfig.paymentElementOptions}
        savedCardsContainer={
          showSavedCardsContainer
            ? (props) => (
                <SavedCardsContainer
                  {...props}
                  settings={settings}
                  selectedCardId={selectedCardId}
                  savedCardsHandler={savedCardsHandler}
                  addCardText={'+ Add New Payment Details'}
                  onMountCallback={scrollToTop}
                  order="defaultCardFirst"
                />
              )
            : null
        }
        savedCardsPaymentHandler={savedCardsPaymentHandler}
        savedPaymentMethodsContainer={
          showPaymentMethodsContainer ? 
           (props) =>  <SavedPaymentMethodsContainer
           {...props}
           settings={settings}
           selectedPaymentMethodId={selectedPaymentMethodId}
           selectPaymentMethod={selectPaymentMethod}
           addPaymentMethodText={'+ Add New Payment Details'}
           onMountCallback={scrollToTop}
           order="defaultPaymentMethodFirst"
         /> 
           : null
        }
        savedPaymentMethodsHandler={savedPaymentMethodsHandler}
        showPaymentElement={showPaymentElement}
        selectedCardId={selectedCardId}
        selectedPaymentMethodId={selectedPaymentMethodId}
        customContainerA={(props) => (
          <CustomContainerA {...props} settings={settings} />
        )}
        customContainerB={(props) => <CustomContainerB {...props} />}
        purchaseCta={purchaseCTA}
        stripeSuccessCallback={stripeSuccessCallback}
        stripeErrorCallback={stripeErrorCallback}
        emailField={
          isEmailFieldRequired ? (props) => <EmailField {...props} onEmailError={onEmailError} /> : null
        }
        emailValidationCallback={isEmailFieldRequired ? emailValidationCallback : null}
        deviceLayoutManager={(component, props) => (
          <DeviceLayoutManager {...props}>
            {component}
          </DeviceLayoutManager>
        )}
        paymentElementOnReadyCallback={paymentElementOnReadyCallback}
        paymentElementOnChangeCallback={paymentElementOnChangeCallback}
        error={postRefreshError}
      />
    )
  );
};

export default refreshProductPurchaseContext(ConfigurePayment);
